import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
// Import tab components
import HRTeamPerformanceTab from "./HRTeamPerformanceTab";
import CommunicationAnalyticsTab from "./CommunicationAnalyticsTab";
import WorkforceForecastTab from "./WorkforceForecastTab";
import SuccessionPipelineTab from "./SuccessionPipelineTab";
import ProjectTrackerTab from "../shared/ProjectTrackerTab";
import DevelopmentPipelineTab from "../executive/DevelopmentPipelineTab";
import ManagerEnablementTab from "../executive/ManagerEnablementTab";
import ComplianceStatusTab from "../executive/ComplianceStatusTab";
import ProgramsInvestmentTab from "../executive/ProgramsInvestmentTab";
import CommunicationHub from "./CommunicationHub";
import WorkforcePlanningTab from "../executive/WorkforcePlanningTab";
import TransformationTab from "../executive/TransformationTab";
import DeliverablesHubTab from "../executive/DeliverablesHubTab";
import { 
  Loader2, 
  Search,
  Bell,
  Download,
  RefreshCw,
  Users,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Activity,
  X,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  FileText,
  Presentation,
  Mail,
  Target,
  Zap,
  Shield,
  UserCheck,
  BarChart3,
  Lock,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Trophy,
  LogOut
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  { value: "all", label: "All Regions" },
  { value: "usa", label: "USA" },
  { value: "canada", label: "Canada" },
  { value: "jamaica", label: "Jamaica" },
  { value: "barbados", label: "Barbados" },
  { value: "trinidad_tobago", label: "Trinidad & Tobago" },
  { value: "panama", label: "Panama" }
];

const ASSESSMENT_TYPES = [
  { value: "all", label: "All Assessment Types" },
  { value: "self_assessment", label: "Self Assessment" },
  { value: "technical", label: "Technical Assessment" },
  { value: "manager_validation", label: "Manager Validation" },
  { value: "simulation", label: "Hands-On Simulation" }
];

const SEGMENTS = [
  { value: "all", label: "All Segments" },
  { value: "Core Builder", label: "Core Builder" },
  { value: "Emerging Builder", label: "Emerging Builder" },
  { value: "Support Developer", label: "Support Developer" },
  { value: "Transition Candidate", label: "Transition Candidate" }
];

const SEGMENT_COLORS = {
  "Core Builder": { bg: '#63C465', text: 'white' },
  "Emerging Builder": { bg: '#00AEEF', text: 'white' },
  "Support Developer": { bg: '#9ca3af', text: 'white' },
  "Transition Candidate": { bg: '#ef4444', text: 'white' }
};

// Mini Sparkline Component
const Sparkline = ({ data, color = "#63C465", height = 32 }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const width = 80;
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * (height - 4);
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Segment Badge Component
const SegmentBadge = ({ segment }) => {
  if (!segment) return <span className="text-xs text-[#9ca3af]">—</span>;
  const colors = SEGMENT_COLORS[segment] || { bg: '#e5e5e5', text: '#3b3b3b' };
  return (
    <span 
      className="px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap"
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      {segment}
    </span>
  );
};

export default function CHRODashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [briSummary, setBriSummary] = useState(null);
  const [weeklyTrend, setWeeklyTrend] = useState(null);
  const [activity, setActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  
  // Filters
  const [regionFilter, setRegionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Fetch all data
  const fetchData = useCallback(async () => {
    const token = localStorage.getItem("chroToken") || localStorage.getItem("authToken");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch stats - use dashboard-stats endpoint
      const statsResponse = await axios.get(`${API}/executive/dashboard-stats`, { headers });
      setStats(statsResponse.data);
      
      // Fetch BRI summary
      try {
        const briResponse = await axios.get(`${API}/executive/bri-summary`, { headers });
        setBriSummary(briResponse.data);
      } catch (e) {
        setBriSummary({ hasData: false });
      }
      
      // Fetch weekly trend
      try {
        const trendResponse = await axios.get(`${API}/executive/weekly-trend`, { headers });
        setWeeklyTrend(trendResponse.data);
      } catch (e) {
        setWeeklyTrend(null);
      }
      
      // Fetch employees
      const employeesResponse = await axios.get(`${API}/executive/employees`, { 
        headers,
        params: { region: regionFilter !== 'all' ? regionFilter : undefined }
      });
      setEmployees(employeesResponse.data.employees || []);
      
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, regionFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("chroToken");
    localStorage.removeItem("authToken");
    navigate("/login");
  };

  const dismissAlert = (index) => {
    setDismissedAlerts([...dismissedAlerts, index]);
  };

  // Helper functions
  const getTrendIcon = (current, previous) => {
    if (current > previous) return <TrendingUp className="w-3 h-3 text-[#63C465]" />;
    if (current < previous) return <TrendingDown className="w-3 h-3 text-[#ef4444]" />;
    return <Minus className="w-3 h-3 text-[#9ca3af]" />;
  };

  const getTrendColor = (current, previous) => {
    if (current > previous) return 'text-[#63C465]';
    if (current < previous) return 'text-[#ef4444]';
    return 'text-[#9ca3af]';
  };

  const notStartedPercentage = stats?.total ? Math.round(((stats?.pending || 0) / stats.total) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00AEEF] mx-auto mb-3" />
          <p className="text-[#9ca3af] text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const visibleAlerts = alerts.filter((_, i) => !dismissedAlerts.includes(i));

  return (
    <div className="min-h-screen bg-[#f8f9fa]" data-testid="chro-dashboard">
      {/* Header - Same as Executive Dashboard */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#e5e7eb]">
        <div className="max-w-[1600px] mx-auto px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#63C465] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">S</span>
              </div>
              <span className="text-lg font-semibold text-[#3b3b3b] hidden sm:inline">
                HR Intelligence
              </span>
            </div>

            {/* Center - Search */}
            <div className="flex-1 max-w-md hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
                <Input
                  placeholder="Search by name, role, region..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="pl-10 bg-[#f8f9fa] border-[#e5e7eb] text-[#3b3b3b] placeholder:text-[#9ca3af] focus:border-[#00AEEF] h-9"
                  data-testid="chro-search"
                />
              </div>
            </div>

            {/* Right */}
            <div className="flex items-center gap-3">
              <Select value={regionFilter} onValueChange={(v) => { setRegionFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[130px] bg-white border-[#e5e7eb] text-[#3b3b3b] text-sm h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REGIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="ghost" size="icon" className="text-[#3b3b3b] hover:text-[#3b3b3b] hover:bg-[#f3f4f6] relative h-9 w-9">
                <Bell className="w-5 h-5" />
                {visibleAlerts.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#ef4444] rounded-full text-[10px] text-white flex items-center justify-center">
                    {visibleAlerts.length}
                  </span>
                )}
              </Button>

              <Select defaultValue="export">
                <SelectTrigger className="w-[100px] bg-white border-[#e5e7eb] text-[#3b3b3b] text-sm h-9">
                  <Download className="w-4 h-4 mr-1" />
                  <span>Export</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="export" disabled>Export Options</SelectItem>
                  <SelectItem value="pdf">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Summary PDF
                    </div>
                  </SelectItem>
                  <SelectItem value="ppt">
                    <div className="flex items-center gap-2">
                      <Presentation className="w-4 h-4" />
                      PowerPoint
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>

              <div className="w-9 h-9 bg-[#00AEEF] rounded-full flex items-center justify-center text-sm font-semibold text-white">
                PW
              </div>
              
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-[#3b3b3b] hover:text-[#3b3b3b] hover:bg-[#f3f4f6] h-9 w-9"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs - Same as Executive Dashboard */}
      <div className="bg-white border-b border-[#e5e7eb]">
        <div className="max-w-[1600px] mx-auto px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-12 bg-transparent border-0 gap-1 w-full justify-start">
              <TabsTrigger 
                value="overview" 
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#3b3b3b]"
                data-testid="tab-overview"
              >
                Overview
              </TabsTrigger>
              <TabsTrigger 
                value="talent-intelligence"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#3b3b3b]"
                data-testid="tab-talent-intelligence"
              >
                Talent Intelligence
              </TabsTrigger>
              <TabsTrigger 
                value="profiles"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#3b3b3b]"
                data-testid="tab-profiles"
              >
                Profiles
              </TabsTrigger>
              <TabsTrigger 
                value="workforce-planning"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#3b3b3b]"
                data-testid="tab-workforce-planning"
              >
                Workforce Planning
              </TabsTrigger>
              <TabsTrigger 
                value="transformation"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#3b3b3b]"
                data-testid="tab-transformation"
              >
                Transformation
              </TabsTrigger>
              <TabsTrigger 
                value="communications"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#3b3b3b]"
                data-testid="tab-communications"
              >
                Communication
              </TabsTrigger>
              <TabsTrigger 
                value="deliverables"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#3b3b3b]"
                data-testid="tab-deliverables"
              >
                Deliverables
              </TabsTrigger>
              <TabsTrigger 
                value="development-pipeline"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#3b3b3b]"
                data-testid="tab-development-pipeline"
              >
                Dev Pipeline
              </TabsTrigger>
              <TabsTrigger 
                value="manager-enablement"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#3b3b3b]"
                data-testid="tab-manager-enablement"
              >
                Managers
              </TabsTrigger>
              <TabsTrigger 
                value="compliance"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#3b3b3b]"
                data-testid="tab-compliance"
              >
                Compliance
              </TabsTrigger>
              <TabsTrigger 
                value="programs"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#3b3b3b]"
                data-testid="tab-programs"
              >
                Programs
              </TabsTrigger>
              <TabsTrigger 
                value="project-tracker"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#3b3b3b]"
                data-testid="tab-project-tracker"
              >
                Project
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Alert Banner */}
      {activeTab === "overview" && visibleAlerts.map((alert, index) => (
        <div 
          key={index}
          className="bg-red-50 border-b border-red-200 px-6 py-3"
        >
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="text-sm text-red-800">
                {alert.message}
              </span>
              <Button variant="link" size="sm" className="text-red-700 p-0 h-auto">
                {alert.action}
              </Button>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 text-red-600 hover:text-red-800"
              onClick={() => dismissAlert(index)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}

      {/* Overview Tab Content */}
      {activeTab === "overview" && (
        <main className="max-w-[1600px] mx-auto px-6 py-6">
          {/* Row 1: Primary KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            {/* Professionals */}
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer border-[#e5e7eb]" data-testid="kpi-professionals">
              <CardContent className="p-4">
                <p className="text-[11px] font-medium text-[#9ca3af] uppercase tracking-wider mb-1">
                  Professionals
                </p>
                <p className="text-3xl font-bold text-[#3b3b3b]">
                  {stats?.total || 0}
                </p>
                <Progress value={stats?.completionRate || 0} className="h-1.5 mt-2" />
                <p className="text-xs text-[#9ca3af] mt-1">
                  <CheckCircle2 className="w-3 h-3 inline mr-1 text-[#63C465]" />
                  {stats?.completed || 0} Complete
                </p>
              </CardContent>
            </Card>

            {/* Completed */}
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow border-[#e5e7eb]" data-testid="kpi-completed">
              <CardContent className="p-4">
                <p className="text-[11px] font-medium text-[#9ca3af] uppercase tracking-wider mb-1">
                  Completed
                </p>
                <p className="text-3xl font-bold text-[#3b3b3b]">
                  {stats?.completed || 0}
                </p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs text-[#9ca3af]">
                    All time completions
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* In Progress */}
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow border-[#e5e7eb]" data-testid="kpi-in-progress">
              <CardContent className="p-4">
                <p className="text-[11px] font-medium text-[#9ca3af] uppercase tracking-wider mb-1">
                  In Progress
                </p>
                <div className="flex items-center justify-between">
                  <p className="text-3xl font-bold text-[#00AEEF]">
                    {stats?.started || 0}
                  </p>
                  {weeklyTrend && <Sparkline data={weeklyTrend.trends?.inProgress} color="#00AEEF" />}
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <span className="w-2 h-2 bg-[#63C465] rounded-full animate-pulse" />
                  <span className="text-xs text-[#63C465]">Active</span>
                </div>
              </CardContent>
            </Card>

            {/* Not Started */}
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow border-[#e5e7eb]" data-testid="kpi-not-started">
              <CardContent className="p-4">
                <p className="text-[11px] font-medium text-[#9ca3af] uppercase tracking-wider mb-1">
                  Not Started
                </p>
                <p className={`text-3xl font-bold ${
                  notStartedPercentage > 50 ? 'text-[#ef4444]' : 'text-[#3b3b3b]'
                }`}>
                  {stats?.pending || 0}
                </p>
                <p className={`text-xs mt-2 ${
                  notStartedPercentage > 50 ? 'text-[#ef4444]' : 'text-[#9ca3af]'
                }`}>
                  {notStartedPercentage}% of total
                </p>
              </CardContent>
            </Card>

            {/* Completion Rate */}
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow border-[#e5e7eb]" data-testid="kpi-completion-rate">
              <CardContent className="p-4">
                <p className="text-[11px] font-medium text-[#9ca3af] uppercase tracking-wider mb-1">
                  Completion Rate
                </p>
                <p className={`text-3xl font-bold ${
                  (stats?.completionRate || 0) >= 75 ? 'text-[#63C465]' :
                  (stats?.completionRate || 0) >= 50 ? 'text-[#3b3b3b]' : 'text-[#ef4444]'
                }`}>
                  {stats?.completionRate || 0}%
                </p>
                <p className="text-xs text-[#9ca3af] mt-2">
                  <Target className="w-3 h-3 inline mr-1" />
                  Completion Rate
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Builder Readiness Summary (BRI) */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {/* Average BRI Score */}
            <Card className={`shadow-sm border-[#e5e7eb] ${briSummary?.hasData ? 'bg-white' : 'bg-[#f8f9fa]'}`} data-testid="kpi-bri-avg">
              <CardContent className="p-4">
                <p className="text-[11px] font-medium text-[#9ca3af] uppercase tracking-wider mb-1">
                  Average BRI Score
                </p>
                {briSummary?.hasData ? (
                  <>
                    <p className="text-3xl font-bold text-[#3b3b3b]">
                      {briSummary?.average || 0}
                    </p>
                    <p className="text-xs text-[#9ca3af] mt-2">
                      of 100 maximum
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-[#9ca3af] mt-2">Awaiting data</p>
                )}
              </CardContent>
            </Card>

            {/* Core Builders */}
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow border-[#e5e7eb]" data-testid="kpi-core-builders">
              <CardContent className="p-4">
                <p className="text-[11px] font-medium text-[#9ca3af] uppercase tracking-wider mb-1">
                  Core Builders
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-[#63C465]">
                    {briSummary?.coreBuilders?.count || 0}
                  </p>
                  <span className="text-sm text-[#3b3b3b]">
                    ({briSummary?.coreBuilders?.percentage || 0}%)
                  </span>
                </div>
                <p className="text-xs text-[#9ca3af] mt-2">
                  <Trophy className="w-3 h-3 inline mr-1 text-[#63C465]" />
                  Top performers
                </p>
              </CardContent>
            </Card>

            {/* Emerging Builders */}
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow border-[#e5e7eb]" data-testid="kpi-emerging">
              <CardContent className="p-4">
                <p className="text-[11px] font-medium text-[#9ca3af] uppercase tracking-wider mb-1">
                  Emerging Builders
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-[#00AEEF]">
                    {briSummary?.emergingBuilders?.count || 0}
                  </p>
                  <span className="text-sm text-[#3b3b3b]">
                    ({briSummary?.emergingBuilders?.percentage || 0}%)
                  </span>
                </div>
                <p className="text-xs text-[#9ca3af] mt-2">
                  <TrendingUp className="w-3 h-3 inline mr-1 text-[#00AEEF]" />
                  High potential
                </p>
              </CardContent>
            </Card>

            {/* Support Developers */}
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow border-[#e5e7eb]" data-testid="kpi-support">
              <CardContent className="p-4">
                <p className="text-[11px] font-medium text-[#9ca3af] uppercase tracking-wider mb-1">
                  Support Developers
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-[#3b3b3b]">
                    {briSummary?.supportDevelopers?.count || 0}
                  </p>
                  <span className="text-sm text-[#3b3b3b]">
                    ({briSummary?.supportDevelopers?.percentage || 0}%)
                  </span>
                </div>
                <p className="text-xs text-[#9ca3af] mt-2">
                  Development focus
                </p>
              </CardContent>
            </Card>

            {/* Transition Candidates */}
            <Card className="bg-white shadow-sm hover:shadow-md transition-shadow border-[#e5e7eb]" data-testid="kpi-transition">
              <CardContent className="p-4">
                <p className="text-[11px] font-medium text-[#9ca3af] uppercase tracking-wider mb-1">
                  Transition Candidates
                </p>
                <div className="flex items-baseline gap-2">
                  <p className={`text-3xl font-bold ${(briSummary?.transitionCandidates?.percentage || 0) > 10 ? 'text-[#ef4444]' : 'text-[#3b3b3b]'}`}>
                    {briSummary?.transitionCandidates?.count || 0}
                  </p>
                  <span className="text-sm text-[#3b3b3b]">
                    ({briSummary?.transitionCandidates?.percentage || 0}%)
                  </span>
                </div>
                <p className={`text-xs mt-2 ${(briSummary?.transitionCandidates?.percentage || 0) > 10 ? 'text-[#ef4444]' : 'text-[#9ca3af]'}`}>
                  {(briSummary?.transitionCandidates?.percentage || 0) > 10 ? 'Requires attention' : 'On track'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Row 3: Regional Progress */}
          <Card className="bg-white shadow-sm mb-6 border-[#e5e7eb]">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#3b3b3b]">Regional Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { name: 'USA', key: 'usa', total: stats?.byRegion?.usa?.total || 0, completed: stats?.byRegion?.usa?.completed || 0 },
                  { name: 'Canada', key: 'canada', total: stats?.byRegion?.canada?.total || 0, completed: stats?.byRegion?.canada?.completed || 0 },
                  { name: 'Jamaica', key: 'jamaica', total: stats?.byRegion?.jamaica?.total || 0, completed: stats?.byRegion?.jamaica?.completed || 0 },
                  { name: 'Barbados', key: 'barbados', total: stats?.byRegion?.barbados?.total || 0, completed: stats?.byRegion?.barbados?.completed || 0 },
                  { name: 'T&T', key: 'trinidad_tobago', total: stats?.byRegion?.trinidad_tobago?.total || 0, completed: stats?.byRegion?.trinidad_tobago?.completed || 0 },
                  { name: 'Panama', key: 'panama', total: stats?.byRegion?.panama?.total || 0, completed: stats?.byRegion?.panama?.completed || 0 }
                ].map((region, i) => {
                  const rate = region.total > 0 ? Math.round((region.completed / region.total) * 100) : 0;
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-[#3b3b3b]">{region.name}</span>
                        <span className="text-xs text-[#9ca3af]">{region.completed}/{region.total}</span>
                      </div>
                      <div className="h-8 bg-[#f3f4f6] rounded overflow-hidden flex items-center">
                        <div 
                          className="h-full bg-[#63C465] flex items-center justify-end pr-2"
                          style={{ width: `${Math.max(rate, 5)}%` }}
                        >
                          <span className="text-xs font-medium text-white">{rate}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Row 4: Recent Activity */}
          <Card className="bg-white shadow-sm border-[#e5e7eb]">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#3b3b3b]">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[200px]">
                <div className="divide-y divide-[#f3f4f6]">
                  {employees.slice(0, 10).map((emp, i) => (
                    <div key={i} className="flex items-center gap-3 py-3">
                      <div className={`w-2 h-2 rounded-full ${
                        emp.status === 'completed' ? 'bg-[#63C465]' :
                        emp.status === 'started' ? 'bg-[#00AEEF]' : 'bg-[#9ca3af]'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#3b3b3b]">{emp.fullName} - {emp.jobTitle}</p>
                        <p className="text-xs text-[#9ca3af]">
                          {emp.status === 'completed' ? 'Completed assessment' : 
                           emp.status === 'started' ? 'In progress' : 'Not started'}
                        </p>
                      </div>
                      <SegmentBadge segment={emp.segment} />
                    </div>
                  ))}
                  {employees.length === 0 && (
                    <p className="text-sm text-[#9ca3af] py-4 text-center">No recent activity</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </main>
      )}

      {/* Other Tabs */}
      {activeTab === "talent-intelligence" && (
        <main className="max-w-[1600px] mx-auto px-6 py-6">
          <Card className="bg-white shadow-sm border-[#e5e7eb]">
            <CardHeader>
              <CardTitle className="text-[#3b3b3b]">Talent Intelligence</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#9ca3af]">Talent analytics and insights coming soon.</p>
            </CardContent>
          </Card>
        </main>
      )}

      {activeTab === "profiles" && (
        <main className="max-w-[1600px] mx-auto px-6 py-6">
          <Card className="bg-white shadow-sm border-[#e5e7eb]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-[#3b3b3b]">Employee Profiles</CardTitle>
                <p className="text-sm text-[#9ca3af]">{employees.length} professionals</p>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#e5e7eb] bg-[#f8f9fa]">
                      <th className="text-left p-3 text-xs font-semibold text-[#9ca3af] uppercase tracking-wide">Employee</th>
                      <th className="text-left p-3 text-xs font-semibold text-[#9ca3af] uppercase tracking-wide">Department</th>
                      <th className="text-left p-3 text-xs font-semibold text-[#9ca3af] uppercase tracking-wide">Region</th>
                      <th className="text-left p-3 text-xs font-semibold text-[#9ca3af] uppercase tracking-wide">Status</th>
                      <th className="text-left p-3 text-xs font-semibold text-[#9ca3af] uppercase tracking-wide">BRI</th>
                      <th className="text-left p-3 text-xs font-semibold text-[#9ca3af] uppercase tracking-wide">Segment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.slice(0, 20).map((emp) => (
                      <tr key={emp.id} className="border-b border-[#f3f4f6] hover:bg-[#f8f9fa]">
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#00AEEF] rounded-full flex items-center justify-center text-white text-xs font-medium">
                              {emp.fullName?.split(' ').map(n => n[0]).join('').slice(0,2) || 'U'}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#3b3b3b]">{emp.fullName}</p>
                              <p className="text-xs text-[#9ca3af]">{emp.jobTitle}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-[#3b3b3b]">{emp.department || '—'}</td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs border-[#e5e7eb] text-[#3b3b3b]">
                            {emp.region?.replace('_', ' ') || '—'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge className={`text-xs ${
                            emp.status === 'completed' ? 'bg-[#63C465]/10 text-[#4fa651]' :
                            emp.status === 'started' ? 'bg-[#00AEEF]/10 text-[#00AEEF]' :
                            'bg-[#f3f4f6] text-[#9ca3af]'
                          }`}>
                            {emp.status === 'completed' ? 'Completed' : emp.status === 'started' ? 'In Progress' : 'Not Started'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <span className={`text-sm font-semibold ${
                            emp.briScore >= 75 ? 'text-[#63C465]' :
                            emp.briScore >= 60 ? 'text-[#3b3b3b]' :
                            emp.briScore ? 'text-[#ef4444]' : 'text-[#9ca3af]'
                          }`}>
                            {emp.briScore || '—'}
                          </span>
                        </td>
                        <td className="p-3">
                          <SegmentBadge segment={emp.segment} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </main>
      )}

      {activeTab === "workforce-planning" && <WorkforcePlanningTab />}
      {activeTab === "transformation" && <TransformationTab />}
      {activeTab === "communications" && <CommunicationHub />}
      {activeTab === "deliverables" && <DeliverablesHubTab />}
      {activeTab === "development-pipeline" && <DevelopmentPipelineTab />}
      {activeTab === "manager-enablement" && <ManagerEnablementTab />}
      {activeTab === "compliance" && <ComplianceStatusTab />}
      {activeTab === "programs" && <ProgramsInvestmentTab />}
      {activeTab === "project-tracker" && <ProjectTrackerTab userRole="chro" />}

      {/* Footer */}
      <footer className="max-w-[1600px] mx-auto px-6 py-4 text-center">
        <p className="text-xs text-[#9ca3af]">© 2026 THCO</p>
      </footer>
    </div>
  );
}
